import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import BookingPage from "./pages/BookingPage";
import BusinessPage from "./pages/BusinessPage";
import DriverPage from "./pages/DriverPage";
import SubcontractorPartnerPage from "./pages/SubcontractorPartnerPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import TrackPage from "./pages/TrackPage";
import ReviewsPage from "./pages/ReviewsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import EmbeddedCheckoutPage from "./pages/EmbeddedCheckoutPage";
import CityLandingPage from "./pages/CityLandingPage";
import RoutePage from "./pages/RoutePage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import BusinessSignupPage from "./pages/BusinessSignupPage";
import PricingPage from "./pages/PricingPage";
import QuoteRequestPage from "./pages/QuoteRequestPage";
import QuoteAgreementSignPage from "./pages/QuoteAgreementSignPage";
import BusinessTermsPage from "./pages/BusinessTermsPage";
import LoginSelectorPage from "./pages/LoginSelectorPage";
import EmployeeLoginPage from "./pages/EmployeeLoginPage";
import EmployeeDashboardPage from "./pages/EmployeeDashboardPage";
import EmployeeChangePasswordPage from "./pages/EmployeeChangePasswordPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import AccessibilityPage from "./pages/AccessibilityPage";
import DataDeletionPage from "./pages/DataDeletionPage";
import ESGPage from "./pages/ESGPage";
import ClosedDaysPage from "./pages/ClosedDaysPage";
import WhistleblowerPage from "./pages/WhistleblowerPage";
import GenfindLandingPage from "./pages/GenfindLandingPage";
import MyPagePage, { MagicAuthPage } from "./pages/MyPagePage";
import EnCityLandingPage from "./pages/EnCityLandingPage";
import OnboardingPage from "./pages/OnboardingPage";
import CustomerLoginPage from "./pages/CustomerLoginPage";
import PakkeshopPartnerPage from "./pages/PakkeshopPartnerPage";
import PakkeshopPortalPage from "./pages/PakkeshopPortalPage";
import FindPickupPointPage from "./pages/FindPickupPointPage";
import WidgetDocsPage from "./pages/WidgetDocsPage";
import { ROUTES } from "./data/routes";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminForgotPasswordPage from "./pages/AdminForgotPasswordPage";
import AdminResetPasswordPage from "./pages/AdminResetPasswordPage";
import ContractSignPage from "./pages/ContractSignPage";
import MissingDocsUploadPage from "./pages/MissingDocsUploadPage";
import B2BSignPage from "./pages/B2BSignPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import DriverLoginPage from "./pages/DriverLoginPage";
import DriverDashboardPage from "./pages/DriverDashboardPage";
import DriverOnboardingPage from "./pages/DriverOnboardingPage";
import DriverMyRoutes from "./pages/DriverMyRoutes";
import DriverCagePickupPage from "./pages/DriverCagePickupPage";
import DriverMyPerformance from "./pages/DriverMyPerformance";
import DriverRouteMap from "./pages/DriverRouteMap";
import DriverVagtplan from "./pages/DriverVagtplan";
import DriverFollowupPage from "./pages/DriverFollowupPage";
import SubcontractorFollowupPage from "./pages/subcontractor/SubcontractorFollowupPage";
import SubcontractorLoginPage from "./pages/SubcontractorLoginPage";
import SubcontractorSetPasswordPage from "./pages/SubcontractorSetPasswordPage";
import SubcontractorPortalPage from "./pages/SubcontractorPortalPage";
import VognmandOnboardingPage from "./pages/VognmandOnboardingPage";
import VognmandAkkordPage from "./pages/VognmandAkkordPage";
import BusinessLoginPage from "./pages/business/BusinessLoginPage";
import BusinessForgotPasswordPage from "./pages/business/BusinessForgotPasswordPage";
import BusinessSetPasswordPage from "./pages/business/BusinessSetPasswordPage";
import BusinessLayout from "./pages/business/BusinessLayout";
import BusinessDashboardPage from "./pages/business/BusinessDashboardPage";
import BusinessPickupLocationsPage from "./pages/business/BusinessPickupLocationsPage";
import BusinessUsersPage from "./pages/business/BusinessUsersPage";
import BusinessChangePasswordPage from "./pages/business/BusinessChangePasswordPage";
import BusinessSettingsPage from "./pages/business/BusinessSettingsPage";
import BusinessShipmentsListPage from "./pages/business/BusinessShipmentsListPage";
import BusinessShipmentCreatePage from "./pages/business/BusinessShipmentCreatePage";
import BusinessEsgReportsPage from "./pages/business/BusinessEsgReportsPage";
import BusinessShipmentDetailPage from "./pages/business/BusinessShipmentDetailPage";
import BusinessLiveTrackingPage from "./pages/business/BusinessLiveTrackingPage";
import BusinessApiPage from "./pages/business/BusinessApiPage";
import BusinessImportPage from "./pages/business/BusinessImportPage";
import BusinessManifestsPage from "./pages/business/BusinessManifestsPage";
import BusinessFailedReturnsPage from "./pages/business/BusinessFailedReturnsPage";
import BusinessInvoicePage from "./pages/business/BusinessInvoicePage";
import BusinessBillingPage from "./pages/business/BusinessBillingPage";
import BusinessTermsAcceptancePage from "./pages/business/BusinessTermsAcceptancePage";
import BusinessRateCardPage from "./pages/business/BusinessRateCardPage";
import BusinessClaimsPage from "./pages/business/BusinessClaimsPage";
import PublicFuldmagtPage from "./pages/PublicFuldmagtPage";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import ChatWidget from "./components/ChatWidget";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { isNative, initNativePush } from "./lib/native";
import { initCustomerNativePush, getRememberedEmail } from "./lib/customer-native";
import { initShopNativePush } from "./lib/shop-native";
import { LangProvider } from "./lib/i18n";
import { trackPageView } from "./lib/tracking";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageView(pathname);
  }, [pathname]);
  return null;
};

const ToasterShellInner = () => {
  const { pathname } = useLocation();
  const portalView = pathname.startsWith("/driver") || pathname.startsWith("/vognmand") || pathname.startsWith("/business") || pathname.startsWith("/track") || pathname.startsWith("/employee");
  return (
    <Toaster
      position={portalView ? "top-center" : "top-right"}
      richColors
      closeButton
      offset={portalView ? 80 : undefined}
      duration={portalView ? 2200 : undefined}
    />
  );
};

const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
    <ChatWidget />
  </>
);

function App() {
  React.useEffect(() => {
    // When wrapped in Capacitor (iOS/Android app), initialise native push.
    // We have TWO native apps: Driver-app (loads /driver) and Customer-app (loads /).
    // Detect which one we're in by inspecting the appId via @capacitor/app.
    if (!isNative()) return;
    (async () => {
      let appId = "";
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        appId = info?.id || "";
      } catch (e) {
        console.warn("Capacitor App.getInfo failed:", e);
      }
      // Driver app
      if (appId === "dk.emakurer.driver" || window.location.pathname.startsWith("/driver")) {
        const getToken = () => localStorage.getItem("ema_driver_token");
        initNativePush(getToken);
        return;
      }
      // Shop-staff app (EMA Pickup Point)
      if (appId === "dk.emakurer.shop" || window.location.pathname.startsWith("/pakkeshop")) {
        initShopNativePush();
        return;
      }
      // Customer app — bind push token to the most recently-known email (from booking flow)
      const email = getRememberedEmail();
      initCustomerNativePush(email);
    })();
  }, []);
  return (
    <div className="App">
      <LangProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ToasterShellInner />
        <Routes>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/underskriv/:token" element={<ContractSignPage />} />
          <Route path="/upload-dokumenter/:token" element={<MissingDocsUploadPage />} />
          <Route path="/b2b-underskriv/:token" element={<B2BSignPage />} />
          <Route path="/driver/login" element={<DriverLoginPage />} />
          <Route path="/driver/onboarding" element={<DriverOnboardingPage />} />
          <Route path="/driver/my-routes" element={<DriverMyRoutes />} />
          <Route path="/driver/cages" element={<DriverCagePickupPage />} />
          <Route path="/driver/my-performance" element={<DriverMyPerformance />} />
          <Route path="/driver/routes/:route_id/map" element={<DriverRouteMap />} />
          <Route path="/driver/vagtplan" element={<DriverVagtplan />} />
          <Route path="/driver/followup" element={<DriverFollowupPage />} />
          <Route path="/driver" element={<DriverDashboardPage />} />
          <Route path="/vognmand/login" element={<SubcontractorLoginPage />} />
          <Route path="/vognmand/saet-password" element={<SubcontractorSetPasswordPage />} />
          <Route path="/vognmand/onboarding" element={<VognmandOnboardingPage />} />
          <Route path="/vognmand" element={<SubcontractorPortalPage />} />
          <Route path="/vognmand/akkord" element={<VognmandAkkordPage />} />
          <Route path="/vognmand/followup" element={<SubcontractorFollowupPage />} />
          <Route path="/subcontractor/followup" element={<SubcontractorFollowupPage />} />

          {/* B2B Customer Portal (Sprint 2A) */}
          <Route path="/business/login" element={<BusinessLoginPage />} />
          <Route path="/fuldmagt/:token" element={<PublicFuldmagtPage />} />
          <Route path="/business/forgot-password" element={<BusinessForgotPasswordPage />} />
          <Route path="/business/set-password" element={<BusinessSetPasswordPage />} />
          <Route path="/business/reset-password" element={<BusinessSetPasswordPage />} />

          {/* Public Tracking (Sprint 2C) */}
          <Route path="/track/:id" element={<PublicTrackingPage />} />
          <Route path="/business" element={<BusinessLayout />}>
            <Route index element={<BusinessDashboardPage />} />
            <Route path="dashboard" element={<BusinessDashboardPage />} />
            <Route path="shipments" element={<BusinessShipmentsListPage />} />
            <Route path="shipments/:id" element={<BusinessShipmentDetailPage />} />
            <Route path="create" element={<BusinessShipmentCreatePage />} />
            <Route path="api" element={<BusinessApiPage />} />
            <Route path="import" element={<BusinessImportPage />} />
            <Route path="import/:batchId" element={<BusinessImportPage />} />
            <Route path="manifests" element={<BusinessManifestsPage />} />
            <Route path="failed-and-returns" element={<BusinessFailedReturnsPage />} />
            <Route path="invoice" element={<BusinessInvoicePage />} />
            <Route path="billing" element={<BusinessBillingPage />} />
            <Route path="terms" element={<BusinessTermsAcceptancePage />} />
            <Route path="rate-card" element={<BusinessRateCardPage />} />
            <Route path="claims" element={<BusinessClaimsPage />} />
            <Route path="pickup-locations" element={<BusinessPickupLocationsPage />} />
            <Route path="users" element={<BusinessUsersPage />} />
            <Route path="change-password" element={<BusinessChangePasswordPage />} />
            <Route path="settings" element={<BusinessSettingsPage />} />
            <Route path="esg" element={<BusinessEsgReportsPage />} />
            <Route path="live-tracking" element={<BusinessLiveTrackingPage />} />
          </Route>
          <Route path="/genfind" element={<GenfindLandingPage />} />
          <Route path="/auth/magic" element={<MagicAuthPage />} />
          <Route path="/min-side" element={<MyPagePage />} />
          <Route path="/*" element={
            <PublicLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/om-os" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/book" element={<BookingPage />} />
                <Route path="/erhverv" element={<BusinessPage />} />
                <Route path="/bliv-chauffoer" element={<DriverPage />} />
                <Route path="/vognmand-partner" element={<SubcontractorPartnerPage />} />
                <Route path="/kontakt" element={<ContactPage />} />
                <Route path="/handelsbetingelser" element={<TermsPage />} />
                <Route path="/track" element={<TrackPage />} />
                <Route path="/anmeldelser" element={<ReviewsPage />} />
                <Route path="/kurer-koebenhavn" element={<CityLandingPage citySlug="koebenhavn" />} />
                <Route path="/kurer-roskilde" element={<CityLandingPage citySlug="roskilde" />} />
                <Route path="/kurer-koege" element={<CityLandingPage citySlug="koege" />} />
                <Route path="/kurer-helsingoer" element={<CityLandingPage citySlug="helsingoer" />} />
                <Route path="/kurer-hilleroed" element={<CityLandingPage citySlug="hilleroed" />} />
                <Route path="/kurer-naestved" element={<CityLandingPage citySlug="naestved" />} />
                <Route path="/kurer-slagelse" element={<CityLandingPage citySlug="slagelse" />} />
                <Route path="/kurer-holbaek" element={<CityLandingPage citySlug="holbaek" />} />
                <Route path="/kurer-kalundborg" element={<CityLandingPage citySlug="kalundborg" />} />
                <Route path="/kurer-ringsted" element={<CityLandingPage citySlug="ringsted" />} />
                <Route path="/kurer-frederikssund" element={<CityLandingPage citySlug="frederikssund" />} />
                <Route path="/kurer-nykoebing-f" element={<CityLandingPage citySlug="nykoebing-f" />} />
                {/* English SEO landing pages targeting international customers */}
                <Route path="/en/courier-copenhagen" element={<EnCityLandingPage citySlug="copenhagen" />} />
                <Route path="/en/courier-roskilde" element={<EnCityLandingPage citySlug="roskilde" />} />
                <Route path="/en/courier-helsingor" element={<EnCityLandingPage citySlug="helsingor" />} />
                <Route path="/onboarding/:token" element={<OnboardingPage />} />
                <Route path="/login" element={<CustomerLoginPage />} />
                <Route path="/portal-login" element={<LoginSelectorPage />} />
                <Route path="/portal" element={<LoginSelectorPage />} />
                <Route path="/bliv-pakkeshop" element={<PakkeshopPartnerPage />} />
                <Route path="/bliv-pickup-partner" element={<PakkeshopPartnerPage />} />
                <Route path="/pickup-point" element={<PakkeshopPartnerPage />} />
                <Route path="/find-pickup-point" element={<FindPickupPointPage />} />
                <Route path="/find-pakkeshop" element={<FindPickupPointPage />} />
                <Route path="/widget" element={<WidgetDocsPage />} />
                <Route path="/pakkeshop" element={<PakkeshopPortalPage />} />
                {ROUTES.map((r) => (
                  <Route
                    key={`${r.fromSlug}-${r.toSlug}`}
                    path={`/fra-${r.fromSlug}-til-${r.toSlug}`}
                    element={<RoutePage fromSlug={r.fromSlug} toSlug={r.toSlug} />}
                  />
                ))}
                <Route path="/betaling/success" element={<PaymentSuccessPage />} />
                <Route path="/betaling/:bookingId" element={<EmbeddedCheckoutPage />} />
                <Route path="/blog" element={<BlogIndexPage />} />
                <Route path="/blog/:slug" element={<BlogArticlePage />} />
                <Route path="/erhverv/tilmeld" element={<BusinessSignupPage />} />
                <Route path="/erhverv/faa-tilbud" element={<QuoteRequestPage />} />
                <Route path="/erhverv/aftale/:token" element={<QuoteAgreementSignPage />} />
                {/* /priser is deprecated — redirect 301 to new quote-request page */}
                <Route path="/priser" element={<Navigate to="/erhverv/faa-tilbud" replace />} />
                <Route path="/priser/*" element={<Navigate to="/erhverv/faa-tilbud" replace />} />
                <Route path="/erhvervsbetingelser" element={<BusinessTermsPage />} />
          <Route path="/employee/login" element={<EmployeeLoginPage />} />
          <Route path="/employee/skift-password" element={<EmployeeChangePasswordPage />} />
          <Route path="/employee" element={<EmployeeDashboardPage />} />
                <Route path="/privatlivspolitik" element={<PrivacyPolicyPage />} />
                <Route path="/cookie-politik" element={<CookiePolicyPage />} />
                <Route path="/tilgaengelighed" element={<AccessibilityPage />} />
                <Route path="/data-sletning" element={<DataDeletionPage />} />
                <Route path="/esg" element={<ESGPage />} />
                <Route path="/lukkedage" element={<ClosedDaysPage />} />
                <Route path="/whistleblower" element={<WhistleblowerPage />} />
                <Route path="*" element={<HomePage />} />
              </Routes>
            </PublicLayout>
          } />
        </Routes>
      </BrowserRouter>
      </LangProvider>
      <CookieConsentBanner />
    </div>
  );
}

export default App;
